import ErrorBoundary from '@/components/ErrorBoundary';
import PushOptInPrompt from '@/components/PushOptInPrompt';
import PwaUpdateBanner from '@/components/PwaUpdateBanner';
import PwaRefreshButton from '@/components/PwaRefreshButton';
import { installAxiosErrorReporter, installBearerFallback } from '@/utils/axiosErrorReporter';
import { useEffect, lazy, Suspense } from 'react';
import i18n from '@/i18n';

// iter137 — Install once at module load so every axios call (anywhere)
// auto-reports 4xx/5xx errors to the AI Error Monitor pipeline.
installAxiosErrorReporter();
installBearerFallback();  // iter246a-1 — Bearer fallback for Safari ITP / WebView / tests
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import AccessDeniedListener from '@/security/AccessDeniedListener';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { SettingsProvider } from '@/context/SettingsContext';
import { CallProvider } from '@/context/CallContext';
import { RealtimeProvider } from '@/context/RealtimeContext';
import { SmartUpsellProvider } from '@/context/SmartUpsellContext';
import { TasksProvider } from '@/context/TasksContext';

// iter198 — Lazy-loaded pages (chargées uniquement à la navigation)
// Pages critiques (chargées en priorité — utilisées dès la connexion)
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const FeedPage = lazy(() => import('@/pages/FeedPage'));
const ChatPage = lazy(() => import('@/pages/ChatPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const WatchEarnPage = lazy(() => import('@/pages/WatchEarnPage'));
const IdaPage = lazy(() => import('@/pages/IdaPage'));
const PublicProfilePage = lazy(() => import('@/pages/PublicProfilePage'));
const WalletPage = lazy(() => import('@/pages/WalletPage'));
const ServicesPage = lazy(() => import('@/pages/ServicesPage'));
const IntelligenceHome = lazy(() => import('@/intelligence/IntelligenceHome'));
const LiveHome = lazy(() => import('@/live/LiveHome'));
const LiveRoom = lazy(() => import('@/live/LiveRoom'));
const IntelligenceAdmin = lazy(() => import('@/intelligence/IntelligenceAdmin'));
const IntelModulePage = lazy(() => import('@/intelligence/IntelModulePage'));

// Pages secondaires
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const AdminAccessGate = lazy(() => import('@/pages/AdminAccessGate'));  // iter280
const AdvertiserLayout = lazy(() => import('@/pages/advertise/AdvertiserLayout'));
const AdvertiseLandingPage = lazy(() => import('@/pages/advertise/AdvertiseLandingPage'));
const AdvertiseOnboardingPage = lazy(() => import('@/pages/advertise/AdvertiseOnboardingPage'));
const AdvertiseDashboardPage = lazy(() => import('@/pages/advertise/AdvertiseDashboardPage'));
const AdvertiseCampaignsListPage = lazy(() => import('@/pages/advertise/AdvertiseCampaignsListPage'));
const AdvertiseCampaignDetailPage = lazy(() => import('@/pages/advertise/AdvertiseCampaignDetailPage'));
const AdvertiseCampaignWizardPage = lazy(() => import('@/pages/advertise/AdvertiseCampaignWizardPage'));
const AdvertiseBillingPage = lazy(() => import('@/pages/advertise/AdvertiseBillingPage'));
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const ClaimAccountPage = lazy(() => import('@/pages/ClaimAccountPage'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const ReferralRedirectPage = lazy(() => import('@/pages/ReferralRedirectPage'));
const ReferralPreviewPage = lazy(() => import('@/pages/ReferralPreviewPage'));
const SuperAdminLoginPage = lazy(() => import('@/pages/SuperAdminLoginPage'));
const SuperAdminPage = lazy(() => import('@/pages/SuperAdminPage'));
const ProPage = lazy(() => import('@/pages/ProPage'));
const ReferralPage = lazy(() => import('@/pages/ReferralPage'));
const ConnectPage = lazy(() => import('@/pages/ConnectPage'));
const ConnectRedeemPage = lazy(() => import('@/pages/ConnectRedeemPage'));
const MyTasksPage = lazy(() => import('@/pages/MyTasksPage'));
const CallHistoryPage = lazy(() => import('@/pages/CallHistoryPage'));
const ReelsPage = lazy(() => import('@/pages/ReelsPage'));
const MyReelsPage = lazy(() => import('@/pages/MyReelsPage'));
const LongVideosPage = lazy(() => import('@/pages/LongVideosPage'));
const MyLongVideosPage = lazy(() => import('@/pages/MyLongVideosPage'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const GroupsPage = lazy(() => import('@/pages/GroupsPage'));
const PagesPage = lazy(() => import('@/pages/PagesPage'));
const JPagesHome = lazy(() => import('@/pages/JPagesModule').then((m) => ({ default: m.JPagesHome })));
const JPagesWizard = lazy(() => import('@/pages/JPagesModule').then((m) => ({ default: m.JPagesWizard })));
const JPagesView = lazy(() => import('@/pages/JPagesModule').then((m) => ({ default: m.JPagesView })));
const JPagesRequest = lazy(() => import('@/pages/JPagesRequest'));
const PostDetailPage = lazy(() => import('@/pages/PostDetailPage'));
// iter266 — Feed Explorer (hashtags / mentions / search).
const ExplorePage = lazy(() => import('@/pages/ExplorePage'));
const UserFollowListPage = lazy(() => import('@/pages/UserFollowListPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const FollowRequestsPage = lazy(() => import('@/pages/FollowRequestsPage'));
const DepositReturnPage = lazy(() => import('@/pages/wallet/DepositReturnPage'));
// iter237af — Hubtel Mobile Money (Ghana 🇬🇭) standalone page.
const WalletHubtelMomoPage = lazy(() => import('@/pages/WalletHubtelMomoPage'));
// iter238 — Paystack Ghana pages (strictly additive).
const WalletPaystackPage = lazy(() => import('@/pages/WalletPaystackPage'));
const WalletPaystackResultPage = lazy(() => import('@/pages/WalletPaystackResultPage'));
// MVP-1 iter261d — Wallet Crypto pages (Crypto Staking domain — separate from historical JAPAP wallet)
const WalletCryptoDashboardPage = lazy(() => import('@/pages/WalletDashboardPage'));
const WalletCryptoReceivePage = lazy(() => import('@/pages/wallet/WalletCryptoReceivePage'));
const WalletCryptoStakingPage = lazy(() => import('@/pages/wallet/WalletCryptoStakingPage'));
const WalletCryptoWithdrawPage = lazy(() => import('@/pages/wallet/WalletCryptoWithdrawPage'));
// iter265 — Transferts entre membres (P2P interne).
const WalletCryptoTransferPage = lazy(() => import('@/pages/wallet/WalletCryptoTransferPage'));
const WalletCryptoTransferHistoryPage = lazy(() => import('@/pages/wallet/WalletCryptoTransferHistoryPage'));
const WalletCryptoSwapPage = lazy(() => import('@/pages/wallet/WalletCryptoSwapPage'));
const WalletCryptoSwapHistoryPage = lazy(() => import('@/pages/wallet/WalletCryptoSwapHistoryPage'));
// iter238c — Admin wallet diagnostics (back-office only, additive).
const AdminWalletDiagnosticsPage = lazy(() => import('@/pages/AdminWalletDiagnosticsPage'));
const OwnerTestEmailPage = lazy(() => import('@/pages/admin/OwnerTestEmailPage'));

// iter237n — Legal pages (CGU / CGJ / RGPD) + About.
const CGUPage = lazy(() => import('@/pages/legal/CGUPage'));
// iter259c — Public unsubscribe redirect page (one-click email link).
const UnsubscribePage = lazy(() => import('@/pages/UnsubscribePage'));
const CGJPage = lazy(() => import('@/pages/legal/CGJPage'));
const PrivacyPage = lazy(() => import('@/pages/legal/PrivacyPage'));
const DownloadAndroidPage = lazy(() => import('@/pages/DownloadAndroidPage'));
const PrivacyRedirect = lazy(() => import('@/pages/legal/PrivacyPage').then((m) => ({ default: m.PrivacyRedirect })));
const DeleteAccountPage = lazy(() => import('@/pages/DeleteAccountPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
// iter260a — Press & Media Center (public, additive)
const PressPage = lazy(() => import('@/pages/PressPage'));
// iter259d — JAPAP AI Knowledge Center (public, additive)
const AIKnowledgePage = lazy(() => import('@/pages/AIKnowledgePage'));
// iter259e — JAPAP FAQ (public, additive)
const FAQPage = lazy(() => import('@/pages/FAQPage'));
// iter260a — Magic link verification result page (public, additive)
const VerifyMagicLinkPage = lazy(() => import('@/pages/VerifyMagicLinkPage'));
// iter260b — Country pages `/africa/:country` (public, additive)
const CountryPage = lazy(() => import('@/pages/CountryPage'));
// iter260c — Public trending page (public, additive)
const TrendingPage = lazy(() => import('@/pages/TrendingPage'));
// iter260d — Blog IA (public, additive)
const BlogListingPage = lazy(() => import('@/pages/BlogPage').then(m => ({ default: m.BlogListingPage })));
const BlogPostPage = lazy(() => import('@/pages/BlogPage').then(m => ({ default: m.BlogPostPage })));

// Games (groupe lourd — chargé uniquement quand l'utilisateur navigue vers /games)
const GamesModule = lazy(() => import('@/pages/GamesModule'));
const ForecastPage = lazy(() => import('@/pages/ForecastPage'));
const WheelFortunePage = lazy(() => import('@/pages/WheelFortunePage'));
const CareerLeaderboardPage = lazy(() => import('@/pages/CareerLeaderboardPage'));
const QuizJAPAPPage = lazy(() => import('@/pages/QuizJAPAPPage'));
const QuizChampionPage = lazy(() => import('@/pages/QuizChampionPage'));
const QuizChallengesPage = lazy(() => import('@/pages/QuizChallengesPage'));
const QuizChallengePage = lazy(() => import('@/pages/QuizChallengePage'));
const OpenChallengePage = lazy(() => import('@/pages/OpenChallengePage'));        // iter228
const PublicChallengePage = lazy(() => import('@/pages/PublicChallengePage'));    // iter228
const TapChallengePage = lazy(() => import('@/pages/TapChallengePage'));
const DuelPage = lazy(() => import('@/pages/DuelPage'));
const MyDuelsSentPage = lazy(() => import('@/pages/MyDuelsSentPage'));

// Pages publiques spéciales
const PublicRideTrackPage = lazy(() => import('@/pages/PublicRideTrackPage'));
const SupportPage = lazy(() => import('@/pages/SupportPage'));
const PayPage = lazy(() => import('@/pages/PayPage'));
const ConnectHotspotLanding = lazy(() => import('@/pages/ConnectHotspotLanding'));
const CrowdfundingProjectPage = lazy(() => import('@/pages/CrowdfundingProjectPage'));
const CrowdfundingLeaderboardPage = lazy(() => import('@/pages/CrowdfundingLeaderboardPage'));
const CrowdfundingJuryPage = lazy(() => import('@/pages/CrowdfundingJuryPage'));
const MarketplaceProductPage = lazy(() => import('@/pages/MarketplaceProductPage'));
const MarketplaceAdsPage = lazy(() => import('@/pages/MarketplaceAdsPage'));

// Layout (non-lazy car utilisé immédiatement dans ProtectedRoute)
import Layout from '@/components/layout/Layout';
import InstallPWA from '@/components/InstallPWA';
import { CurrencyProvider } from '@/utils/currency';
import useGeoLanguageBootstrap from '@/hooks/useGeoLanguageBootstrap';
import '@/App.css';

/*
  JAPAP MESSENGER — ROUTING (NO DUPLICATES)
  ============================================
  /feed       → HOME / FEED (actualites, posts, stories)
  /chat       → MESSENGER (chat 1-1, groupes, appels)
  /wallet     → WALLET (solde, depot, retrait, transactions)
  /services   → SERVICES HUB (marketplace, food, taxi, etc.)
  /profile    → PROFILE / SETTINGS (info, parametres, KYC)
  /admin      → ADMIN (admin-only, accessible depuis profil)
  
  Chaque fonctionnalite = un seul endroit.
*/

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--jp-bg)' }}>
      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--jp-primary)', borderTopColor: 'transparent' }} />
    </div>
  );
  // Preserve the intended deep link (e.g. /post/xxx or /users/:id/followers)
  // across the login detour so external sharers land on the right page.
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  // Iter83 — superadmin implicitly has all admin capabilities.
  // iter264a — configurable roles (developer/marketing/comptabilite/compliance)
  // also reach /admin; the AdminPage itself filters which tabs they see.
  const ADMIN_ROLES = ['admin', 'superadmin', 'developer', 'marketing', 'comptabilite', 'compliance'];
  if (adminOnly && !ADMIN_ROLES.includes(user.role)) return <Navigate to="/feed" replace />;
  return <Layout>{children}</Layout>;
}

/* iter280 — Route admin protégée par PIN. Vérifie :
   1. utilisateur connecté avec rôle admin
   2. cookie `admin_access_verified` présent (session PIN validée)
   Sans PIN validé, retourne une réponse neutre 404 (aucun leak sur l'existence
   d'une console admin — pas de redirect qui pourrait révéler le mot-clé). */
function NeutralNotFound() {
  return (
    <div style={{ minHeight: '100vh', background: '#0F056B', color: 'rgba(255,255,255,0.85)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'system-ui, -apple-system, sans-serif' }}
         data-testid="neutral-404">
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 72, fontWeight: 900, letterSpacing: -2 }}>404</div>
        <div style={{ fontSize: 14, opacity: 0.6, marginTop: 4 }}>Resource not found</div>
      </div>
    </div>
  );
}

function AdminGuardedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--jp-bg)' }}>
      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--jp-primary)', borderTopColor: 'transparent' }} />
    </div>
  );
  if (!user) return <NeutralNotFound />;
  const ADMIN_ROLES = ['admin', 'superadmin', 'developer', 'marketing', 'comptabilite', 'compliance'];
  if (!ADMIN_ROLES.includes(user.role)) return <NeutralNotFound />;
  // Cookie non-httponly posé par /api/admin/access/verify-pin succès
  const verified = typeof document !== 'undefined'
    && document.cookie.split(';').some(c => c.trim().startsWith('admin_access_verified=1'));
  if (!verified) return <NeutralNotFound />;
  return <Layout>{children}</Layout>;
}

/* Iter83 — Superadmin guard. Does NOT wrap with the normal Layout (the
   superadmin dashboard has its own chrome + dark theme). Invalid/unknown
   roles are redirected to the dynamic login page for the same token. */
function SuperadminDashboardRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  // pathname like /admin230426/dashboard  → token = '230426'
  const token = (location.pathname.split('/')[1] || '').replace(/^admin/, '');
  if (loading) return null;
  if (!user || user.role !== 'superadmin') {
    return <Navigate to={`/admin${token}`} replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--jp-bg)' }}>
      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--jp-primary)', borderTopColor: 'transparent' }} />
    </div>
  );
  // If the user is already logged in and the router-state remembers an
  // intended URL (set by ProtectedRoute), honour it over the default /feed.
  // iter141nine — also honour `?redirect=` query param so shared payment
  // request links (PayPage) survive a login round-trip.
  if (user) {
    const from = location.state?.from;
    const queryRedirect = new URLSearchParams(location.search).get('redirect');
    return <Navigate to={from || queryRedirect || '/feed'} replace />;
  }
  return children;
}

/* Iter83 — Wildcard handler that routes /admin{DDMMYY} and its
   /dashboard sub-path to the superadmin portal, while anything else falls
   back to /feed. This lets the path be literally /admin230426 (no slash)
   even though React Router doesn't parse inline segment params. */
function DynamicAdminOrFallback() {
  const location = useLocation();
  const path = (location.pathname || '').replace(/\/+$/, '');
  // iter274e-fix — Twitter/Instagram-style `/@handle` aliases redirect to
  // the canonical `/u/{handle}` route. React Router 6 does NOT support
  // patterns starting with `@` as a literal path segment, so we catch it
  // here in the catch-all and forward — no 404, no /login flash.
  const atMatch = path.match(/^\/@([A-Za-z0-9_.-]+)$/);
  if (atMatch) return <Navigate to={`/u/${atMatch[1]}`} replace />;
  // iter280 — Ancien /admin{DDMMYY} (Iter83) désactivé. Toute tentative
  // renvoie sur /feed (aucun leak).
  return <Navigate to="/feed" replace />;
}

// iter198 — Spinner léger pour les transitions lazy de pages (adapté 2G/3G)
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--jp-bg)' }}>
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: 'var(--jp-primary)', borderTopColor: 'transparent' }} />
        <p className="text-xs font-['Manrope']" style={{ color: 'var(--jp-text-muted)' }}>{i18n.t('common.loading_ellipsis', { defaultValue: 'Chargement...' })}</p>
      </div>
    </div>
  );
}

function AppRouter() {
  // iter203 — Détection automatique de langue par IP pour visiteurs anonymes.
  // Ne fait rien si l'utilisateur a déjà choisi une langue (localStorage).
  useGeoLanguageBootstrap();
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <Suspense fallback={<PageLoader />}><AuthCallback /></Suspense>;

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Landing page publique */}
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      {/* iter109 — Short-link parrainage : /r/{code} → /register?ref={code}. */}
      <Route path="/r/:code" element={<ReferralRedirectPage />} />
      {/* iter110 — Public conversion landing : /p/{code}. */}
      <Route path="/p/:code" element={<ReferralPreviewPage />} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      {/* iter243c — Curated onboarding claim flow. The token comes from the
          invitation email; the page accepts a password, verifies the email,
          and auto-logs the user in. */}
      <Route path="/claim/:token" element={<ClaimAccountPage />} />
      
      {/* 5 BLOCS PRINCIPAUX — Sans duplication */}
      <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />

      {/* iter265b — Advertiser Portal (public landing + authenticated portal) */}
      <Route path="/advertise" element={<AdvertiseLandingPage />} />
      <Route path="/advertise/onboarding" element={<AdvertiseOnboardingPage />} />
      <Route path="/advertise" element={<ProtectedRoute><AdvertiserLayout /></ProtectedRoute>}>
        <Route path="dashboard" element={<AdvertiseDashboardPage />} />
        <Route path="campaigns" element={<AdvertiseCampaignsListPage />} />
        <Route path="campaigns/new" element={<AdvertiseCampaignWizardPage />} />
        <Route path="campaigns/:id" element={<AdvertiseCampaignDetailPage />} />
        <Route path="billing" element={<AdvertiseBillingPage />} />
      </Route>
      {/* iter274d — /post/:postId is intentionally NOT protected so Google
           crawlers AND anonymous visitors landing from search results can
           see the post. Authenticated users still see it with full reactions.
           The page falls back to /api/public/feed/posts/... when no session.
           iter274e — Optional trailing slug segment for canonical URLs
           `/post/{id}/{slug}`. The slug is ignored at render (postId is the
           source of truth) — it's purely for readable / indexable URLs. */}
      <Route path="/post/:postId" element={<PostDetailPage />} />
      <Route path="/news/:newsSlug" element={<PostDetailPage />} />
      <Route path="/post/:postId/:slug" element={<PostDetailPage />} />
      {/* iter266 — Feed Explorer */}
      <Route path="/explore" element={<ProtectedRoute><ExplorePage /></ProtectedRoute>} />
      <Route path="/users/:userId/followers" element={<ProtectedRoute><UserFollowListPage /></ProtectedRoute>} />
      <Route path="/users/:userId/following" element={<ProtectedRoute><UserFollowListPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/requests" element={<ProtectedRoute><FollowRequestsPage /></ProtectedRoute>} />
      <Route path="/games" element={<ProtectedRoute><GamesModule /></ProtectedRoute>} />
      <Route path="/games/forecast" element={<ProtectedRoute><ForecastPage /></ProtectedRoute>} />
      <Route path="/games/wheel" element={<ProtectedRoute><WheelFortunePage /></ProtectedRoute>} />
      <Route path="/games/career" element={<ProtectedRoute><CareerLeaderboardPage /></ProtectedRoute>} />
      <Route path="/games/quiz" element={<ProtectedRoute><QuizJAPAPPage /></ProtectedRoute>} />
      <Route path="/games/quiz/daily" element={<ProtectedRoute><QuizJAPAPPage /></ProtectedRoute>} />
      <Route path="/games/quiz/champion" element={<ProtectedRoute><QuizChampionPage /></ProtectedRoute>} />
      <Route path="/games/quiz/champion/:country" element={<ProtectedRoute><QuizChampionPage /></ProtectedRoute>} />
      {/* iter228 — Open challenge flow (A creates → A plays → A shares link → B claims). */}
      <Route path="/games/quiz/challenge/new" element={<ProtectedRoute><OpenChallengePage /></ProtectedRoute>} />
      <Route path="/c/:cid" element={<PublicChallengePage />} />
      <Route path="/games/quiz/challenges" element={<ProtectedRoute><QuizChallengesPage /></ProtectedRoute>} />
      <Route path="/games/quiz/challenges/:id" element={<ProtectedRoute><QuizChallengePage /></ProtectedRoute>} />
      <Route path="/games/tap" element={<ProtectedRoute><TapChallengePage /></ProtectedRoute>} />
      <Route path="/duel/:token" element={<DuelPage />} />
      <Route path="/duel/me/sent" element={<ProtectedRoute><MyDuelsSentPage /></ProtectedRoute>} />
      <Route path="/track/:token" element={<PublicRideTrackPage />} />
      <Route path="/pay/:requestId" element={<PayPage />} />
      <Route path="/connect/h/:hotspotId" element={<ConnectHotspotLanding />} />
      <Route path="/crowdfunding/p/:slug" element={<CrowdfundingProjectPage />} />
      <Route path="/crowdfunding/leaderboard" element={<CrowdfundingLeaderboardPage />} />
      <Route path="/crowdfunding/jury" element={<CrowdfundingJuryPage />} />
      <Route path="/crowdfunding" element={<Navigate to="/services?view=crowdfunding" replace />} />
      <Route path="/marketplace/p/:product_id" element={<ProtectedRoute><MarketplaceProductPage /></ProtectedRoute>} />
      <Route path="/marketplace/ads" element={<ProtectedRoute><MarketplaceAdsPage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
      {/* iter195 — CEO : page dédiée de retour Hubtel (clone EAA) */}
      <Route path="/wallet/deposit/return" element={<ProtectedRoute><DepositReturnPage /></ProtectedRoute>} />
      {/* iter237af — Hubtel Mobile Money (Ghana 🇬🇭) — strictly additive */}
      <Route path="/wallet/hubtel-momo" element={<ProtectedRoute><WalletHubtelMomoPage /></ProtectedRoute>} />
      {/* iter238 — Paystack Ghana — strictly additive */}
      <Route path="/wallet/paystack" element={<ProtectedRoute><WalletPaystackPage /></ProtectedRoute>} />
      <Route path="/wallet/paystack/result" element={<ProtectedRoute><WalletPaystackResultPage /></ProtectedRoute>} />
      {/* MVP-1 iter261d — Wallet Crypto (dépôt + staking à venir). Distinct du Wallet JAPAP historique (/wallet). */}
      <Route path="/wallet/crypto" element={<ProtectedRoute><WalletCryptoDashboardPage /></ProtectedRoute>} />
      <Route path="/wallet/crypto/receive" element={<ProtectedRoute><WalletCryptoReceivePage /></ProtectedRoute>} />
      <Route path="/wallet/crypto/staking" element={<ProtectedRoute><WalletCryptoStakingPage /></ProtectedRoute>} />
      <Route path="/wallet/crypto/withdraw" element={<ProtectedRoute><WalletCryptoWithdrawPage /></ProtectedRoute>} />
      <Route path="/wallet/crypto/transfer" element={<ProtectedRoute><WalletCryptoTransferPage /></ProtectedRoute>} />
      <Route path="/wallet/crypto/transfer/history" element={<ProtectedRoute><WalletCryptoTransferHistoryPage /></ProtectedRoute>} />
      <Route path="/wallet/crypto/swap" element={<ProtectedRoute><WalletCryptoSwapPage /></ProtectedRoute>} />
      <Route path="/wallet/crypto/swap/history" element={<ProtectedRoute><WalletCryptoSwapHistoryPage /></ProtectedRoute>} />
      <Route path="/services" element={<ProtectedRoute><ServicesPage /></ProtectedRoute>} />
      <Route path="/intelligence" element={<ProtectedRoute><IntelligenceHome /></ProtectedRoute>} />
      <Route path="/live" element={<ProtectedRoute><LiveHome /></ProtectedRoute>} />
      <Route path="/live/:liveId" element={<ProtectedRoute><LiveRoom /></ProtectedRoute>} />
      <Route path="/intelligence/admin" element={<ProtectedRoute><IntelligenceAdmin /></ProtectedRoute>} />
      <Route path="/intelligence/:moduleKey" element={<ProtectedRoute><IntelModulePage /></ProtectedRoute>} />
      <Route path="/groups" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
      <Route path="/pages" element={<ProtectedRoute><PagesPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/services/watch-earn" element={<ProtectedRoute><WatchEarnPage /></ProtectedRoute>} />
      <Route path="/services/ida" element={<ProtectedRoute><IdaPage /></ProtectedRoute>} />
      <Route path="/services/pages" element={<ProtectedRoute><JPagesHome /></ProtectedRoute>} />
      <Route path="/services/pages/new" element={<ProtectedRoute><JPagesWizard /></ProtectedRoute>} />
      <Route path="/services/pages/requests/:rid" element={<ProtectedRoute><JPagesRequest /></ProtectedRoute>} />
      <Route path="/services/pages/:ref" element={<ProtectedRoute><JPagesView /></ProtectedRoute>} />
      <Route path="/services/pages/:ref/edit" element={<ProtectedRoute><JPagesWizard /></ProtectedRoute>} />
      {/* iter240j — Public profile route (visibility-aware backend). */}
      <Route path="/profile/:username" element={<PublicProfilePage />} />
      {/* iter274e-fix — /u/{username} is the canonical scheme (matches
           sitemap-users.xml and OG endpoints). `/@handle` is redirected
           to `/u/handle` by DynamicAdminOrFallback since React Router 6
           can't match a path starting with `@` as a literal segment. */}
      <Route path="/u/:username" element={<PublicProfilePage />} />
      
      {/* Pro, Notifications, Admin */}
      <Route path="/pro" element={<ProtectedRoute><ProPage /></ProtectedRoute>} />
      <Route path="/referral" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
      <Route path="/connect" element={<ProtectedRoute><ConnectPage /></ProtectedRoute>} />
      <Route path="/connect/redeem" element={<ProtectedRoute><ConnectRedeemPage /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><MyTasksPage /></ProtectedRoute>} />
      <Route path="/calls" element={<ProtectedRoute><CallHistoryPage /></ProtectedRoute>} />
      <Route path="/reels" element={<ProtectedRoute><ReelsPage /></ProtectedRoute>} />
      {/* iter254 — My Reels creator dashboard (paid Boost CTA). */}
      <Route path="/reels/me" element={<ProtectedRoute><MyReelsPage /></ProtectedRoute>} />
      {/* iter217 — viral deep-link: /reels/{reel_id} opens the page
          scrolled to the targeted reel. Same component, parsed via
          useParams() inside ReelsPage. */}
      <Route path="/reels/:reelId" element={<ProtectedRoute><ReelsPage /></ProtectedRoute>} />
      {/* iter255 — Long-form videos (1–20 min), horizontal player. */}
      <Route path="/videos" element={<ProtectedRoute><LongVideosPage /></ProtectedRoute>} />
      <Route path="/videos/me" element={<ProtectedRoute><MyLongVideosPage /></ProtectedRoute>} />
      <Route path="/videos/:videoId" element={<ProtectedRoute><LongVideosPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      {/* iter280 — Ancienne URL /admin nécessite désormais la vérification PIN.
          Sans cookie `admin_access_verified`, on redirige silencieusement vers /feed. */}
      <Route path="/admin" element={<AdminGuardedRoute><AdminPage /></AdminGuardedRoute>} />
      {/* iter280 — Nouvelle URL privée : /bassa/helios/{DDMMYY} (rotation quotidienne UTC).
          Le token est vérifié côté serveur via /api/admin/access/validate-token/{token}. */}
      <Route path="/bassa/helios/:token" element={<AdminAccessGate />} />
      {/* iter238c — Admin wallet diagnostics (back-office only, additive). */}
      <Route path="/admin/wallet/diagnostics" element={<AdminGuardedRoute><AdminWalletDiagnosticsPage /></AdminGuardedRoute>} />
      <Route path="/admin/owner-test-email" element={<AdminGuardedRoute><OwnerTestEmailPage /></AdminGuardedRoute>} />
      {/* iter237n — Pages légales publiques (pas d'auth requise) */}
      <Route path="/legal/cgu" element={<CGUPage />} />
      {/* iter259c — Public unsubscribe redirect (no auth). */}
      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />      <Route path="/legal/conditions-de-jeu" element={<CGJPage />} />
      <Route path="/legal/confidentialite" element={<PrivacyPage />} />
      <Route path="/privacy" element={<PrivacyRedirect />} />
      <Route path="/download/android" element={<DownloadAndroidPage />} />
      <Route path="/privacy/:lang" element={<PrivacyPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<AboutPage />} />
      {/* iter260a — Press & Media Center (public, additive) */}
      <Route path="/press" element={<PressPage />} />
      {/* iter259d — JAPAP AI Knowledge Center (public, additive) */}
      <Route path="/ai" element={<AIKnowledgePage />} />
      {/* iter259e — JAPAP FAQ (public, additive) */}
      <Route path="/faq" element={<FAQPage />} />
      {/* iter260a — Magic link verification landing (public, additive) */}
      <Route path="/verify" element={<VerifyMagicLinkPage />} />
      {/* iter260b — Local country pages (public, additive) */}
      <Route path="/africa/:country" element={<CountryPage />} />
      {/* iter260c — Trending page (public, additive) */}
      <Route path="/trending" element={<TrendingPage />} />
      {/* iter260d — Blog IA (public, additive) */}
      <Route path="/blog" element={<BlogListingPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      {/* Iter83 — Dynamic superadmin portal at /admin{DDMMYY}.
          The path is handled by the catch-all `DynamicAdminOrFallback`
          below so that /admin230426 maps to SuperAdminLoginPage while any
          other unknown URL still falls through to /feed. */}

      {/* Redirect par défaut (capture également /admin{DDMMYY}) */}
      <Route path="*" element={<DynamicAdminOrFallback />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <div className="App">
          <BrowserRouter>
          <AuthProvider>
            <SettingsProvider>
            <CurrencyProvider>
              <RealtimeProvider>
                <SmartUpsellProvider>
                  <CallProvider>
                    <TasksProvider>
                      <AppRouter />
                      <InstallPWA />
                      <PushOptInPrompt />
                      <PwaUpdateBanner />
                      {/* iter239x — Floating PWA refresh button (top-right, always visible) */}
                      <div
                        className="jp-pwa-refresh-pos"
                        style={{ position: 'fixed', zIndex: 9000, pointerEvents: 'auto' }}
                        data-testid="pwa-refresh-floating">
                        <PwaRefreshButton compact />
                      </div>
                      <Toaster
                        position="top-center"
                        richColors
                        closeButton
                        toastOptions={{
                          style: {
                            fontFamily: 'Manrope, sans-serif',
                            border: '1px solid rgba(15,5,107,0.12)',
                          },
                        }}
                      />
                      <AccessDeniedListener />
                    </TasksProvider>
                  </CallProvider>
                </SmartUpsellProvider>
              </RealtimeProvider>
            </CurrencyProvider>
            </SettingsProvider>
          </AuthProvider>
          </BrowserRouter>
        </div>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
